// Order sets, apply side (PROD-2712): the data layer. Reads the caller's active sets and one set's
// detail, and sends the AC5 measurement (one application per apply, then its events).
//
// TELEMETRY NEVER BLOCKS THE CLINICIAN. Every write here is fire and forget: nothing awaits it on
// the clinical path, every error is swallowed (logged to the console in a dev build only), and
// nothing retries. The bodies carry ids and fixed strings only: no patient data, no free text.
import { useQuery } from '@tanstack/react-query';
import { get, post } from '@/shared/api/api-client';
import type {
  CreateOrderSetApplicationBody,
  OrderSetApplicationResult,
  OrderSetApplyDetail,
  OrderSetApplySummary,
  OrderSetEventBody,
  OrderSetEventType,
  OrderSetResolution,
} from '@/types/order-set-apply-api';

/** Same base as the Settings tab (PROD-2710); every call acts on the CALLER's own sets. */
export const ORDER_SETS_APPLY_BASE = '/api/Patients/OrderSets';

export const applyOrderSetsQueryKey = ['encounter', 'order-sets', 'active'] as const;

/** GET /OrderSets?status=active — the rail's list. Disabled unless the feature is on for this user. */
export function useApplyOrderSets(enabled: boolean) {
  return useQuery({
    queryKey: applyOrderSetsQueryKey,
    queryFn: ({ signal }) =>
      get<OrderSetApplySummary[]>(`${ORDER_SETS_APPLY_BASE}?status=active`, { signal }),
    enabled,
  });
}

/**
 * GET /OrderSets/{id}, fresh on every apply (not cached): the set's version and its items are what
 * the application records, so a stale detail would record the wrong version.
 */
export function fetchOrderSetForApply(id: string): Promise<OrderSetApplyDetail> {
  return get<OrderSetApplyDetail>(`${ORDER_SETS_APPLY_BASE}/${encodeURIComponent(id)}`);
}

function devWarn(what: string, err: unknown) {
  if (import.meta.env.DEV) {
    // Dev builds only: a failed measurement write is worth seeing locally, never in the product.
    console.warn(`[order sets] ${what} failed (ignored)`, err);
  }
}

/** POST /OrderSets/{id}/applications → the applicationId, or null on any failure. Never throws. */
export async function createOrderSetApplication(
  setId: string,
  body: CreateOrderSetApplicationBody,
): Promise<string | null> {
  try {
    const res = await post<OrderSetApplicationResult>(
      `${ORDER_SETS_APPLY_BASE}/${encodeURIComponent(setId)}/applications`,
      body,
    );
    return typeof res?.applicationId === 'string' && res.applicationId ? res.applicationId : null;
  } catch (err) {
    devWarn('recording the application', err);
    return null;
  }
}

/** POST /OrderSets/applications/{applicationId}/events → 204. Never throws, never retries. */
export async function sendOrderSetEvent(applicationId: string, body: OrderSetEventBody): Promise<void> {
  try {
    await post<void>(
      `${ORDER_SETS_APPLY_BASE}/applications/${encodeURIComponent(applicationId)}/events`,
      body,
    );
  } catch (err) {
    devWarn(`recording the "${body.type}" event`, err);
  }
}

/**
 * One application's measurement channel. The application POST is started once; every event waits
 * for its id (so an event fired before the 201 lands still reaches it) and is dropped if the POST
 * failed. `once` keys make each event fire at most once for its application.
 */
export interface OrderSetTelemetry {
  emit(type: OrderSetEventType, opts?: { itemId?: string; resolution?: OrderSetResolution; onceKey?: string }): void;
}

export function startOrderSetTelemetry(
  setId: string,
  body: CreateOrderSetApplicationBody | null,
): OrderSetTelemetry {
  // No encounter or patient in hand → nothing is recorded (and nothing waits on it).
  const applicationId: Promise<string | null> = body ? createOrderSetApplication(setId, body) : Promise.resolve(null);
  const sent = new Set<string>();
  return {
    emit(type, opts = {}) {
      const key = opts.onceKey ?? `${type}:${opts.itemId ?? ''}`;
      if (sent.has(key)) return;
      sent.add(key);
      const event: OrderSetEventBody = {
        type,
        ...(opts.itemId ? { itemId: opts.itemId } : {}),
        ...(opts.resolution ? { resolution: opts.resolution } : {}),
      };
      void applicationId.then((id) => (id ? sendOrderSetEvent(id, event) : undefined));
    },
  };
}
